import { ScrollStrategyOptions } from '@angular/cdk/overlay';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { RechazoCamionComponent } from 'src/app/profiles/aridos-mixer/despacho/components/rechazo-camion/rechazo-camion.component';
import { SuccessDialogComponent } from 'src/app/shared/ui/success-dialog/success-dialog.component';
import { OrderService } from 'src/app/services/pedidos/pedidos.service';
import { Order } from 'src/app/shared/models/orders';
import {
  AridosDispatchPresentation,
  getAridosDispatchPresentation,
  getAridosReturnReasonDescriptions,
} from '../../../aridos-dispatch-presentation';

import { from, EMPTY, Observable } from 'rxjs';
import { concatMap, catchError, finalize, take, tap } from 'rxjs/operators';

@Component({
    selector: 'app-pedidos-pendiente-observaciones',
    templateUrl: './pedidos-pendiente-observaciones.component.html',
    styleUrls: ['./pedidos-pendiente-observaciones.component.css'],
    standalone: false
})
export class PedidosPendienteObservacionesComponent implements OnInit {
  @ViewChild('fileInput') fileInput: ElementRef;
  images: { file: File; preview: string }[] = [];

  form = new UntypedFormGroup({
    waterAddition: new UntypedFormControl(false),
    anotherAddition: new UntypedFormControl(false),
    guidingRemarks: new UntypedFormControl(''),
    m3Returned: new UntypedFormControl(''),
    reasonToReturn: new UntypedFormControl(''),
    m3ReturnedPallet: new UntypedFormControl(''),
  });

  returnReasons: string[] = [];

  @ViewChild('modalContent') modalContent!: TemplateRef<any>;
  dialogRef: MatDialogRef<any>;

  @Output() nextStep = new EventEmitter<any>();
  @Output() rejectTruck = new EventEmitter<any>();
  @Input() order: Order;
  presentation: AridosDispatchPresentation;
  isCompletedStatus = false; // status >= 6 => modo completado (solo lectura)
  canContinue = false;       // Continuar solo si status >= 6
  visualStatusIndex = -1;    // ya lo tienes

  currentStep = 1;
  readonly totalSteps = 2;
  /** índice para pintar progreso (0..3). Para status 6 forzamos "todo activo" */

  /** índice seleccionado en la UI (0..3), -1 = sin estado */
  currentStatusIndex = -1;

  isContinuing = false;

  statuses = ['Cargando', 'En Ruta', 'En  Obra', 'Fin Descarga'];

  get operatorCommentsText(): string {
    const comments = this.order?.operatorComments ?? [];
    if (!comments.length) {
      return 'No hay comentarios de los operadores';
    }
    return comments.map((item) => item.comment).filter(Boolean).join(', ');
  }

  constructor(
    private orderService: OrderService,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private scrollStrategyOptions: ScrollStrategyOptions
  ) { }

  // =========================
  //   MAPEOS DE ESTADO
  // =========================
  indexToOrderStatus(index: number): number {
    switch (index) {
      case 0:
        return 2; // Cargando
      case 1:
        return 3; // En ruta
      case 2:
        return 4; // En obra
      case 3:
        return 5; // Fin descarga
      default:
        return 0;
    }
  }

  orderStatusToIndex(status: number): number {
    switch (status) {
      case 2:
        return 0;
      case 3:
        return 1;
      case 4:
        return 2;
      case 5:
        return 3;
      case 6:
        return 3; // ✅ tratar 6 como "Fin Descarga" para que quede todo activo
      default:
        return -1;
    }
  }


  // =========================
  //   LOGICA "PRINCIPAL"
  //   (solo permite avanzar 1 paso)
  // =========================
  onStatusClick(index: number, event: Event): void {
    if (this.currentStep !== 1) return;

    // misma idea del principal:
    // - si currentStatusIndex = -1 => solo se permite 0
    // - si currentStatusIndex >= 0 => solo se permite current + 1
    const canAdvance =
      (this.currentStatusIndex === -1 && index === 0) ||
      (this.currentStatusIndex >= 0 && index === this.currentStatusIndex + 1);

    if (!canAdvance) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    this.setStatus$(index).subscribe({
      next: () => { },
      error: (e) => console.error(e),
    });
  }

  // =========================
  //   ACTUALIZAR ESTADO (BACKEND)
  // =========================
private setStatus$(index: number): Observable<any> {
  const newStatus = this.indexToOrderStatus(index);

  // ✅ UI inmediata: al hacer click en i, se pinta 0..i
  this.order.status = newStatus;
  this.currentStatusIndex = index;
  this.visualStatusIndex = index;

  // flags derivados
  this.isCompletedStatus = newStatus >= 6;
  this.canContinue = newStatus >= 5;

  return this.orderService
    .updateStatus(
      this.order.tkt.toString(),
      this.order.orderNumber.toString(),
      newStatus
    )
    .pipe(
      tap((resp: any) => {
        // ✅ si el backend retorna status confiable, úsalo
        const backendStatus = resp?.status ?? resp?.manualStatus ?? null;

        
        if (typeof backendStatus === 'number') {
          this.order.status = backendStatus;
          this.applyDerivedStateFromStatus(backendStatus);
        } else {
          // ✅ si no retorna, NO recalcules con manualStatus
          this.applyDerivedStateFromStatus(this.order.status);
        }
      })
    );
}




  // =========================
  //   CONTINUAR: "CLICK INTERNO" SECUENCIAL
  // =========================
  private runStatusesToLast$(): Observable<any> {
    const lastIndex = this.statuses.length - 1;
    const startIndex =
      this.currentStatusIndex < 0 ? 0 : this.currentStatusIndex + 1;

    if (this.currentStatusIndex >= lastIndex) return EMPTY;

    const indices: number[] = [];
    for (let i = startIndex; i <= lastIndex; i++) indices.push(i);

    return from(indices).pipe(concatMap((i) => this.setStatus$(i)));
  }

private applyDerivedStateFromStatus(status: number) {
  this.isCompletedStatus = status >= 6;

  // Continuar: si quieres activarlo desde "Fin Descarga" (5) o desde 6, déjalo así
  this.canContinue = status >= 5;

  let idx = this.orderStatusToIndex(status);
  if (idx < -1 || idx >= this.statuses.length) idx = -1;

  this.currentStatusIndex = idx;

  // ✅ REGLA DE PINTADO:
  // - si completado => todo activo
  // - si no => pintar 0..currentStatusIndex
  this.visualStatusIndex = this.isCompletedStatus
    ? (this.statuses.length - 1)
    : this.currentStatusIndex;
}


  continueAridoStep1(): void {
    if (this.currentStep !== 1) return;
    if (!this.canContinue) return; // ✅

    const lastIndex = this.statuses.length - 1;

    if (this.currentStatusIndex >= lastIndex) {
      this.nextStepArido();
      return;
    }

    this.isContinuing = true;

    this.runStatusesToLast$()
      .pipe(
        catchError((err) => {
          //console.error('Error avanzando estados internamente', err);
          return EMPTY;
        }),
        finalize(() => (this.isContinuing = false))
      )
      .subscribe({
        complete: () => {
          if (this.currentStatusIndex >= lastIndex) {
            this.nextStepArido();
          }
        },
      });
  }


  // =========================
  //   INIT
  // =========================
  ngOnInit(): void {
    this.route.queryParams.subscribe((params) => {
      const tkt = params.tkt;

      this.orderService.getOrder(tkt).subscribe((data) => {
        this.order = data[0];
        this.presentation = getAridosDispatchPresentation(this.order);

        //console.log('order', this.order.status, this.order.manualStatus);

        const status = Math.max(this.order.status, this.order.manualStatus);

        // ✅ una sola fuente de verdad para flags + índices
        this.applyDerivedStateFromStatus(status);

        this.getReturnReasons();
      });
    });
  }



  // =========================
  //   ARCHIVOS / FORM
  // =========================
  openFileSelector() {
    this.fileInput.nativeElement.click();
  }

  onFileSelected(event: any) {
    const files: FileList = event.target.files;
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (!this.isValidFile(file)) continue;
      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.images.push({ file, preview: e.target.result });
      };
      reader.readAsDataURL(file);
    }
    this.fileInput.nativeElement.value = '';
  }

  isValidFile(file: File): boolean {
    const allowedExtensions = ['.jpg', '.jpeg', '.png'];
    const fileNameParts = file.name.split('.');
    const fileExtension = `.${fileNameParts[fileNameParts.length - 1]}`;
    return allowedExtensions.includes(fileExtension.toLowerCase());
  }

  confirm() {
    this.dialogRef = this.dialog.open(this.modalContent, {
      maxWidth: '90%',
      scrollStrategy: this.scrollStrategyOptions.noop(),
    });
  }

  async submit() {
    this.closeModal();

    const tkt = this.order.tkt.toString();
    const orderNumber = this.order.orderNumber.toString();
    const payload = {
      tkt,
      orderNumber,
      waterAddition: this.form.value.waterAddition,
      anotherAddition: this.form.value.anotherAddition,
      guidingRemarks: this.form.value.guidingRemarks,
      m3Returned: this.form.value.m3Returned,
      reasonToReturn: this.form.value.reasonToReturn,
      m3ReturnedPallet: this.form.value.m3ReturnedPallet,
      // preview is already a base64 data URL from FileReader.readAsDataURL
      files: this.images.map(img => ({ name: img.file.name, base64: img.preview })),
    };

    await this.orderService.setExtraValues(
      tkt,
      orderNumber,
      payload.waterAddition,
      payload.anotherAddition,
      payload.guidingRemarks,
      payload.m3Returned,
      payload.reasonToReturn,
      payload.m3ReturnedPallet,
      this.images.map(img => img.file),
      payload.files
    ).pipe(take(1)).toPromise();

    this.nextStep.emit();
  }

  rejectTruckLocal(): void {
    const ref = this.dialog.open(RechazoCamionComponent, {
      width: '900px',
      disableClose: true,
      data: { order: this.order },
    });

    ref.afterClosed().subscribe((res) => {
      if (res?.action === 'success') {
        this.dialog.open(SuccessDialogComponent, {
          width: '520px',
          maxWidth: '95vw',
          disableClose: true,
          data: {
            title: 'Rechazo enviado con éxito',
            message: 'Esta acción no se puede deshacer',
          },
        });
      }
    });
  }

  getReturnReasons() {
    this.orderService.getRejectReasons().subscribe((data) => {
      this.returnReasons = getAridosReturnReasonDescriptions(data, this.order);
    });
  }

  closeModal() {
    this.dialogRef.close();
  }

  numberOnly(event): boolean {
    const charCode = event.which ? event.which : event.keyCode;
    return (charCode >= 48 && charCode <= 57) || charCode === 44;
  }

  onInputChange(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    const inputValue = Number(inputElement.value);

    if (inputValue > this.order.actualQuantityShipped) {
      this.form.controls.m3Returned.setValue(this.order.actualQuantityShipped);
    } else if (inputValue < 0) {
      this.form.controls.m3Returned.setValue(this.order.actualQuantityShipped);
    }
  }

  onInpuMajorCero(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    const inputValue = Number(inputElement.value);

    if (inputValue < 0) {
      this.form.controls.m3ReturnedPallet.setValue(0);
    }
  }

  removeImage(img: { file: File; preview: string }) {
    this.images = this.images.filter((i) => i !== img);
  }

  nextStepArido(): void {
    if (this.currentStep < this.totalSteps) this.currentStep++;
  }

  prevStepArido(): void {
    if (this.currentStep > 1) this.currentStep--;
  }
}

